// NewTimesheetFlow — shared "create a manual timesheet" entry point.
// Renders a trigger (via render-prop children) then a two-step flow:
//   1. Choose what the time is logged against (Workshop / Onsite Labour / Travel).
//   2. Open the seeded Timesheet form for that pseudo-asset.
// Used by the Timesheet Review page (Add Timesheet button) and the top-bar
// WeeklyHoursButton (+ icon) so both share one identical flow.
//
// catNo/assetId are written verbatim onto asset.asset_timesheet, so they must
// match the pseudo-asset records: Workshop = 'WORKSHOP' (legacy uppercase),
// Onsite Labour = 'OnsiteLabour', Travel = 'Travel'.

import React, { useState } from 'react';
import {
  Dialog,
  DialogContent,
  Box,
  Button,
  Typography,
} from '@mui/material';
import HandymanIcon from '@mui/icons-material/Handyman';
import PlaceIcon from '@mui/icons-material/Place';
import FlightTakeoffIcon from '@mui/icons-material/FlightTakeoff';
import Timesheet from '../../pages/edit/timesheetAsset';

export const TIMESHEET_ASSET_OPTIONS = [
  { key: 'WORKSHOP', label: 'Workshop', catNo: 'WORKSHOP', assetId: 'WORKSHOP', icon: <HandymanIcon /> },
  { key: 'ONSITE', label: 'Onsite Labour', catNo: 'OnsiteLabour', assetId: 'OnsiteLabour', icon: <PlaceIcon /> },
  { key: 'TRAVEL', label: 'Travel', catNo: 'Travel', assetId: 'Travel', icon: <FlightTakeoffIcon /> },
];

function seedTimesheet(option) {
  return {
    catNo: option.catNo,
    assetId: option.assetId,
    id: null,
    timeSpent: null,
    notes: null,
    workPerformedOn: null,
    status: 'WIP',
    jobNumber: '-',
    jobCard: null,
    purchaseOrder: null,
    lineItem: '-',
  };
}

export default function NewTimesheetFlow({ children }) {
  const [choiceOpen, setChoiceOpen] = useState(false);
  const [timesheetOpen, setTimesheetOpen] = useState(false);
  const [foundTimesheet, setFoundTimesheet] = useState(null);

  const startNew = () => setChoiceOpen(true);

  const chooseOption = (option) => {
    setFoundTimesheet(seedTimesheet(option));
    setChoiceOpen(false);
    setTimesheetOpen(true);
  };

  const closeTimesheet = () => {
    setTimesheetOpen(false);
    setFoundTimesheet(null);
  };

  return (
    <>
      {children(startNew)}

      {/* Step 1: what is this time logged against? */}
      <Dialog open={choiceOpen} onClose={() => setChoiceOpen(false)} maxWidth="xs" fullWidth>
        <DialogContent sx={{ p: 0 }}>
          <Box sx={{ px: 3, py: 1.5, borderBottom: '1px solid var(--border)', background: 'var(--surface)' }}>
            <Typography variant="h6" sx={{ fontWeight: 700 }}>New Timesheet</Typography>
            <Typography variant="caption" sx={{ color: 'var(--muted)' }}>What is this time logged against?</Typography>
          </Box>
          <Box sx={{ px: 3, py: 2, display: 'flex', flexDirection: 'column', gap: 1.5 }}>
            {TIMESHEET_ASSET_OPTIONS.map((opt) => (
              <Button
                key={opt.key}
                fullWidth
                variant="outlined"
                startIcon={opt.icon}
                onClick={() => chooseOption(opt)}
                sx={{ justifyContent: 'flex-start', py: 1.25 }}
              >
                {opt.label}
              </Button>
            ))}
          </Box>
          <Box sx={{ px: 3, py: 1.5, borderTop: '1px solid var(--border)', background: 'var(--surface)' }}>
            <Button
              variant="outlined"
              color="inherit"
              sx={{ color: 'var(--muted)', borderColor: 'var(--border-strong)' }}
              onClick={() => setChoiceOpen(false)}
            >
              Cancel
            </Button>
          </Box>
        </DialogContent>
      </Dialog>

      {/* Step 2: the timesheet form, seeded with the chosen pseudo-asset */}
      <Dialog open={timesheetOpen} onClose={closeTimesheet} maxWidth="sm" fullWidth>
        <DialogContent sx={{ p: 0 }}>
          {foundTimesheet && <Timesheet foundTimesheet={foundTimesheet} onCancel={closeTimesheet} />}
        </DialogContent>
      </Dialog>
    </>
  );
}
